/**
 * EnemyCombat — extracted combat/targeting helpers.
 * Each function takes the Enemy instance as the first parameter.
 */
import { EnemyType, EnemyWeaponType, EnemyWeaponDef, AGGRO_RANGE, TILE_SIZE, TileType } from '@/config/GameConfig';
import { GOD, getEnemyAnchor } from '@/config/GodMode';
import { EventBus, GameEvents } from '@/events/EventBus';
import { fuzzyAggroTarget } from '@/systems/ThreatAssessor';
import { SoundManager } from '@/systems/SoundManager';
import { hasLineOfSight } from '@/systems/LineOfSight';
import { MAP_GRID } from '@/map/MapData';
import { Robot } from '@/entities/Robot';
import { Enemy } from '@/entities/Enemy';
import type { RobotTarget } from '@/entities/Enemy';

// ────────────────────────────────────────────────────────
// Shooting
// ────────────────────────────────────────────────────────

/**
 * Update ranged shooting: finds closest robot in range, fires projectile.
 * Called each frame. Only one weapon can fire at a time (global lock shared with CQC).
 */
export function updateShooting(
  enemy: Enemy,
  dt: number,
  robots: RobotTarget[],
  baseTarget?: { x: number; y: number },
): void {
  if (enemy.weaponDefs.length === 0 || !enemy.alive || enemy.isCloaked || enemy.stunTimer > 0) return;
  // Confused enemies can't aim (hit by invisible attacker — scanning wildly)
  if (enemy.camoConfusionTimer > 0) return;
  // In melee contact: enemy is brawling — can't aim and shoot at the same time
  if (enemy.inMeleeContact) return;

  // Tick not-firing timer: tracks how long this enemy has a target but hasn't shot
  if (enemy.firingTarget) enemy.notFiringTimer += dt;
  else enemy.notFiringTimer = 0;
  // Too close to any target robot: can't aim projectiles at point-blank range
  const minShootDist = enemy.collisionRadius + Enemy.MELEE_RANGE * GOD.meleeRangeMult;
  for (const r of robots) {
    if (!r.alive) continue;
    const rdx = r.x - enemy.body.x;
    const rdy = r.y - enemy.body.y;
    if (rdx * rdx + rdy * rdy < minShootDist * minShootDist) return;
  }

  for (let i = 0; i < enemy.weaponDefs.length; i++) {
    const wDef = enemy.weaponDefs[i];

    // Handle burst continuation (fires remaining burst shots even during lock)
    if (wDef.type === EnemyWeaponType.BURST && enemy.burstRemaining[i] > 0) {
      enemy.burstDelayTimer[i] -= dt * 1000; // convert to ms
      if (enemy.burstDelayTimer[i] <= 0) {
        enemy.burstRemaining[i]--;
        enemy.burstDelayTimer[i] = wDef.burstDelay ?? 100;
        const burstRobot = findClosestRobot(enemy, robots, wDef.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult);
        const burstTarget = burstRobot ? resolveAimPoint(enemy, burstRobot) : (baseTarget ?? null);
        if (burstTarget) fireWeapon(enemy, i, wDef, burstTarget);
      }
      continue;
    }

    // Normal cooldown tick (always ticks, even when locked)
    enemy.weaponTimers[i] -= dt;
    if (enemy.weaponTimers[i] > 0) continue;

    // Global lock: can't fire if another weapon (or CQC) just fired
    if (enemy.weaponLockTimer > 0) continue;

    // Find closest target (robot OR base) within weapon range
    const effectiveRange = wDef.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult;
    const closestRobot = findClosestRobot(enemy, robots, effectiveRange);
    let baseDist = Infinity;
    if (baseTarget) {
      const bx = baseTarget.x - enemy.body.x;
      const by = baseTarget.y - enemy.body.y;
      baseDist = Math.sqrt(bx * bx + by * by);
    }
    const baseInRange = baseDist <= effectiveRange;

    let target: { x: number; y: number } | null = null;
    if (closestRobot && baseInRange) {
      // Pick whichever is closer
      const rx = closestRobot.x - enemy.body.x;
      const ry = closestRobot.y - enemy.body.y;
      const robotDist = Math.sqrt(rx * rx + ry * ry);
      // When targeting robot, resolve aim at exposed module if smart enough
      target = robotDist <= baseDist ? resolveAimPoint(enemy, closestRobot) : baseTarget!;
    } else {
      target = closestRobot ? resolveAimPoint(enemy, closestRobot) : (baseInRange ? baseTarget! : null);
    }
    if (!target) continue;

    // Fire! Set both weapon cooldown and global lock
    enemy.weaponTimers[i] = wDef.cooldown;
    enemy.weaponLockTimer = Math.max(Enemy.WEAPON_LOCK_MIN, wDef.cooldown * GOD.weaponCooldownStagger);

    if (wDef.type === EnemyWeaponType.BURST) {
      enemy.burstRemaining[i] = (wDef.burstCount ?? 3) - 1;
      enemy.burstDelayTimer[i] = wDef.burstDelay ?? 100;
    }

    fireWeapon(enemy, i, wDef, target);
    enemy.notFiringTimer = 0; // successfully fired — reset close-in pressure
    break; // only one weapon fires per frame
  }
}

// ────────────────────────────────────────────────────────
// Target finding
// ────────────────────────────────────────────────────────

/** Find closest alive robot within range (with wall LOS check for ground enemies). */
export function findClosestRobot(
  enemy: Enemy,
  robots: RobotTarget[],
  range: number,
): RobotTarget | null {
  let best: RobotTarget | null = null;
  let bestDist = range;
  const checkLOS = !enemy.isFlyer; // flyers ignore walls
  for (const r of robots) {
    if (!r.alive || r.isCamoActive) continue;
    const dx = r.x - enemy.body.x;
    const dy = r.y - enemy.body.y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d < bestDist) {
      // Ground enemies can't shoot through walls
      if (checkLOS && !hasLineOfSight(enemy.body.x, enemy.body.y, r.x, r.y)) continue;
      bestDist = d; best = r;
    }
  }
  return best;
}

/** Probability that this enemy type aims at an exposed module instead of center mass. */
export function getModuleTargetChance(enemy: Enemy): number {
  switch (enemy.enemyType) {
    case EnemyType.PHANTOM: return 0.7;  // stealth assassin, very precise
    case EnemyType.BRUTE:   return 0.5;  // heavy hitter, targets weapons
    case EnemyType.TANK:    return 0.4;  // methodical, decent aim
    case EnemyType.SPECTER: return 0.6;  // flying elite
    case EnemyType.BOMBER:  return 0.3;  // splash damage, some precision
    case EnemyType.DRONE:   return 0.2;  // basic flyer
    case EnemyType.GRUNT:   return 0.1;  // basic soldier, mostly center mass
    case EnemyType.RUNNER:  return 0.05; // fast, sprays and prays
    case EnemyType.SWARM:   return 0.0;  // mindless swarm
    case EnemyType.WASP:    return 0.15; // small, some precision
    case EnemyType.HIVE:    return 0.1;  // lobbed acid, low precision
    default: return 0.1;
  }
}

/**
 * Resolve final aim point for a robot target.
 * Smart enemies aim at exposed modules instead of center.
 */
export function resolveAimPoint(enemy: Enemy, robot: RobotTarget): { x: number; y: number } {
  const smartChance = getModuleTargetChance(enemy);
  if (smartChance > 0 && robot.getExposedTarget && Math.random() < smartChance) {
    const modulePos = robot.getExposedTarget();
    if (modulePos) return modulePos;
  }
  return { x: robot.x, y: robot.y };
}

/** Fire a specific weapon toward target, spawning projectile from sprite edge. */
export function fireWeapon(
  enemy: Enemy,
  weaponIdx: number,
  wDef: EnemyWeaponDef,
  target: { x: number; y: number },
): void {
  const dx = target.x - enemy.body.x;
  const dy = target.y - enemy.body.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 1) return;

  // HP-based miss chance: 0% at full HP → 15% at 0 HP
  const hpRatio = enemy.maxHp > 0 ? enemy.hp / enemy.maxHp : 1;
  const hpMiss = (1 - hpRatio) * 0.15;

  // Distance-based miss chance: 0% at melee → 10% at max weapon range
  const effectiveRange = wDef.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult;
  const distMiss = effectiveRange > 0
    ? Math.min(dist / effectiveRange, 1) * 0.10
    : 0;

  // Combined miss chance
  const totalMiss = distMiss + hpMiss;
  if (totalMiss > 0 && Math.random() < totalMiss) return;

  // Spawn point: if anchor is set, use it as absolute offset from center;
  // otherwise default to sprite edge toward target.
  const nx = dx / dist;
  const ny = dy / dist;
  const anchor = getEnemyAnchor(enemy.enemyType);
  const hasAnchor = anchor.ox !== 0 || anchor.oy !== 0;
  const sx = hasAnchor
    ? enemy.body.x + anchor.ox
    : enemy.body.x + nx * enemy.visualRadius;
  const sy = hasAnchor
    ? enemy.body.y + anchor.oy
    : enemy.body.y + ny * enemy.visualRadius;

  // Play attack animation (reverts to walk on complete)
  enemy.playAnim('attack');
  enemy.body.once('animationcomplete', () => {
    if (enemy.alive && enemy.animState === 'attack') enemy.playAnim('walk');
  });

  EventBus.emit(GameEvents.ENEMY_FIRED, {
    sx, sy,
    tx: target.x, ty: target.y,
    damage: enemy.weaponDamages[weaponIdx],
    speed: wDef.speed,
    color: wDef.color,
    range: wDef.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult,
    weaponType: wDef.type,
    enemyType: enemy.enemyType,
    enemyId: enemy.id,
    // Flame-specific data
    coneAngle: wDef.coneAngle,
    flameDuration: wDef.flameDuration,
    // Acid catapult data
    splashRadius: wDef.splashRadius,
    poisonDuration: wDef.poisonDuration,
    poisonDps: wDef.poisonDps,
  });
}

// ────────────────────────────────────────────────────────
// Aggro
// ────────────────────────────────────────────────────────

/**
 * Feed nearby robot positions so aggro enemies can rush them.
 * Called each frame from RobotManager before enemy.update().
 */
export function updateAggroTarget(enemy: Enemy, robotPositions: { x: number; y: number }[], robots?: Robot[], dt: number = 1 / 60): void {
  // Cache robot refs for weapon-range engagement in updateGround
  enemy.nearbyRobots = robots ?? [];

  if (!enemy.canAggro || !enemy.alive || enemy.camoConfusionTimer > 0) {
    enemy.aggroTarget = null;
    enemy.inMeleeContact = false;
    return;
  }

  const hadAggro = enemy.aggroTarget !== null;

  let target: { x: number; y: number } | null = null;

  const checkLOS = !enemy.isFlyer; // flyers ignore walls

  // Stuck detection: if enemy has aggro but can't fire AND isn't making progress, disengage
  // If enemy has a firingTarget it's actively fighting — not stuck (arc formation is normal)
  if (enemy.aggroTarget && !enemy.firingTarget) {
    const dx = enemy.aggroTarget.x - enemy.body.x;
    const dy = enemy.aggroTarget.y - enemy.body.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    // Progress = distance decreased by at least 2px since last check
    if (dist < enemy.aggroLastDist - 2) {
      enemy.aggroStuckTimer = 0;
      enemy.aggroLastDist = dist;
      enemy.aggroDetourPath = null; // real progress — clear detour
    } else {
      enemy.aggroStuckTimer += dt;
    }
    // Stuck too long → drop aggro, resume path to base
    if (enemy.aggroStuckTimer > GOD.wallDisengageTime) {
      enemy.aggroTarget = null;
      enemy.aggroStuckTimer = 0;
      enemy.aggroLastDist = Infinity;
      enemy.notFiringTimer = 0;
      enemy.inMeleeContact = false;
      enemy.aggroDetourPath = null;
      enemy.resyncToNearestWaypoint();
      enemy.alertShownForCurrentAggro = false;
      return;
    }
  } else {
    // No aggro OR has firingTarget (actively fighting) → reset stuck timer
    enemy.aggroStuckTimer = 0;
    enemy.aggroLastDist = Infinity;
  }

  // Ranged enemies: always engage the closest robot within weapon range
  // regardless of base pull — they should stop and shoot, not walk past
  if (enemy.weaponDefs.length > 0 && robots && robots.length > 0) {
    const maxRange = Math.max(...enemy.weaponDefs.map(w => w.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult));
    let bestDist = maxRange;
    for (const r of robots) {
      if (!r.alive || r.isCamoActive) continue;
      const dx = r.x - enemy.body.x;
      const dy = r.y - enemy.body.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < bestDist) {
        // Ground enemies can't see through walls
        if (checkLOS && !hasLineOfSight(enemy.body.x, enemy.body.y, r.x, r.y)) continue;
        bestDist = d; target = { x: r.x, y: r.y };
      }
    }
  }

  // If no robot in weapon range, use fuzzy threat-weighted targeting for aggro rush
  const aggroRange = enemy.weaponDefs.length > 0
    ? Math.max(...enemy.weaponDefs.map(w => w.range * GOD.enemyWeaponRangeMult * enemy.typeRangeMult)) * 1.25
    : AGGRO_RANGE;
  if (!target) {
    // Filter out camouflaged robots — enemies can't detect them
    // Ground enemies also filter out robots behind walls
    const visibleRobots = robots?.filter(r => {
      if (r.isCamoActive) return false;
      if (checkLOS && !hasLineOfSight(enemy.body.x, enemy.body.y, r.x, r.y)) return false;
      return true;
    });
    if (visibleRobots && visibleRobots.length > 0) {
      target = fuzzyAggroTarget(
        enemy.body.x, enemy.body.y,
        visibleRobots,
        aggroRange,
        enemy.getPathProgress(),
      );
    }
  }

  // Aggro drop grace period: don't instantly drop aggro, allow brief out-of-range moments
  // (prevents flicker when robot advances and repulsion pushes enemy out of range for 1-2 frames)
  if (hadAggro && !target) {
    if (enemy.aggroGraceTimer <= 0) {
      enemy.aggroGraceTimer = 0.3; // 300ms grace before truly dropping aggro
    }
    enemy.aggroGraceTimer -= dt;
    if (enemy.aggroGraceTimer > 0) {
      // Keep previous aggro target during grace period
      return;
    }
    // Grace expired — truly drop aggro
    enemy.inMeleeContact = false;
    enemy.aggroDetourPath = null;
    enemy.resyncToNearestWaypoint();
    enemy.alertShownForCurrentAggro = false;
  } else if (target) {
    enemy.aggroGraceTimer = 0; // reset grace when target re-acquired
  }

  // Alert on first aggro of this session (cooldown: 2s minimum between popups)
  const now = enemy.scene.time.now;
  const alertCooldown = 2000; // ms
  if (!hadAggro && target && !enemy.alertShownForCurrentAggro && (now - enemy.lastAlertTime) > alertCooldown) {
    enemy.showAlertPopup();
    enemy.alertShownForCurrentAggro = true;
    enemy.lastAlertTime = now;
    if (Math.random() < 0.3) SoundManager.play('enemyGrowl');
    // Lateral nudge on aggro start: small random push perpendicular to target direction
    // Helps unstick enemies that are flush against a wall facing the robot
    if (!enemy.isFlyer) {
      const adx = target.x - enemy.body.x;
      const ady = target.y - enemy.body.y;
      const aDist = Math.sqrt(adx * adx + ady * ady);
      if (aDist > 1) {
        const nudge = 6; // pixels
        // Try both perpendicular directions, pick one that lands on a walkable tile
        for (const sign of (Math.random() < 0.5 ? [1, -1] : [-1, 1])) {
          const nx = enemy.body.x + (-ady / aDist) * sign * nudge;
          const ny = enemy.body.y + (adx / aDist) * sign * nudge;
          const col = Math.floor(nx / TILE_SIZE);
          const row = Math.floor(ny / TILE_SIZE);
          const tile = MAP_GRID[row]?.[col];
          if (tile === TileType.PATH || tile === TileType.PLACEMENT || tile === TileType.BROKEN_PLACEMENT || tile === TileType.SPAWN || tile === TileType.MUD || tile === TileType.GRILLE) {
            enemy.body.setPosition(nx, ny);
            break;
          }
        }
      }
    }
  }

  enemy.aggroTarget = target;
  // Reset melee contact flag — collision system will set it again this frame if needed
  enemy.inMeleeContact = false;
}

// ────────────────────────────────────────────────────────
// Base siege
// ────────────────────────────────────────────────────────

/** Called when enemy first reaches the base. */
export function onReachedBase(enemy: Enemy): void {
  if (enemy.attackingBase) return; // already sieging
  enemy.attackingBase = true;
  enemy.reachedBase = true;
  enemy.meleeBaseTimer = enemy.meleeCooldownDuration;
  // Enemy stays alive and must be killed by robots
}

/** Periodic melee damage to player base for melee-only enemies (no weapons). */
export function updateBaseSiege(enemy: Enemy, dt: number): void {
  if (enemy.weaponDefs.length > 0) return; // ranged enemies attack via updateShooting
  if (enemy.stunTimer > 0) return; // can't attack while stunned
  enemy.meleeBaseTimer -= dt;
  if (enemy.meleeBaseTimer <= 0) {
    enemy.meleeBaseTimer = enemy.meleeCooldownDuration;
    // Emit event — GameScene will apply damage to PlayerBase
    EventBus.emit(GameEvents.ENEMY_REACHED_BASE, enemy);
  }
}
